import { useState, useEffect } from 'react';
import { getAccessToken } from '../utils/auth';
import Editor from '@monaco-editor/react';

function AgentEdit({ agent, onCancel, onUnsavedChange, onViewUsage, onViewTables }) {
  const [config, setConfig] = useState({ display_name: '', description: '', ds_id: '', sp_id: '', mp_id: '', mc_id: '', created: '', modified: '', process_configs: []});
  const [datasources, setDatasources] = useState([]);
  const [processConfigs, setProcessConfigs] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState('TRANSFORM');
  const [transformEnabled, setTransformEnabled] = useState(false);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [showProviderDropdown, setShowProviderDropdown] = useState(false);
  const [modelConnectors, setModelConnectors] = useState([]);
  const [selectedModelConnector, setSelectedModelConnector] = useState('');
  const [showModelConnectorDropdown, setShowModelConnectorDropdown] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [showPromptDropdown, setShowPromptDropdown] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [models, setModels] = useState([]);
  const [jsonEditorContent, setJsonEditorContent] = useState({});
  const [selectedModel, setSelectedModel] = useState('');
  // const [usage, setUsage] = useState({ indexed: 0, last_indexed: null });
  const [usage, setUsage] = useState({});
  const [tablesMetadata, setTablesMetadata] = useState({});
  const [isModified, setIsModified] = useState(false);
  const [showJsonSchemaModal, setShowJsonSchemaModal] = useState(false);
  const [schemaPaths, setSchemaPaths] = useState([]);
  const [selectedPath, setSelectedPath] = useState('');
  const [schemaFields, setSchemaFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [pathFilter, setPathFilter] = useState('');
  const [fieldFilter, setFieldFilter] = useState('');
  const [sampleData, setSampleData] = useState(null);
  const [tableName, setTableName] = useState('');
  const [tableNameError, setTableNameError] = useState(false);
  const [originalConfig, setOriginalConfig] = useState({ display_name: '', description: '', ds_id: '' });
  const [originalJsonEditorContent, setOriginalJsonEditorContent] = useState({});
  const [originalTransformerSettings, setOriginalTransformerSettings] = useState({ sp_id: '', md_id: '', mp_id: '', mc_id: '', new_only: false, transform_tables: [] });
  const [originalOutputType, setOriginalOutputType] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [displayNameError, setDisplayNameError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const [justCreatedAgent, setJustCreatedAgent] = useState(false);
  const [showNoChangesModal, setShowNoChangesModal] = useState(false);
  const [newOnly, setNewOnly] = useState(false);
  const [selectedTables, setSelectedTables] = useState([]);
  const [originalPromptText, setOriginalPromptText] = useState('');
  const [showPromptErrorModal, setShowPromptErrorModal] = useState(false);
  const [promptErrorMessage, setPromptErrorMessage] = useState('');
  const [promptErrorTitle, setPromptErrorTitle] = useState('');
  const [showSaveNewPromptModal, setShowSaveNewPromptModal] = useState(false);
  const [newPromptName, setNewPromptName] = useState('');
  const [newPromptDescription, setNewPromptDescription] = useState('');
  const [selectedOutputType, setSelectedOutputType] = useState('');
  const [showOutputTypeDropdown, setShowOutputTypeDropdown] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testPaths, setTestPaths] = useState([]);
  const [selectedTestPath, setSelectedTestPath] = useState('');
  const [testPathFilter, setTestPathFilter] = useState('');
  const [testResult, setTestResult] = useState('');
  const [testFileListError, setTestFileListError] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testPathsLoading, setTestPathsLoading] = useState(false);
  const [selectedChunkModelConnector, setSelectedChunkModelConnector] = useState('');
  const [selectedChunkModel, setSelectedChunkModel] = useState('');
  const [chunkModels, setChunkModels] = useState([]);
  const [showChunkModelConnectorDropdown, setShowChunkModelConnectorDropdown] = useState(false);
  const [originalChunkSettings, setOriginalChunkSettings] = useState({ mc_id: '', model_id: '' });

  useEffect(() => {
    setTransformEnabled(!!(selectedModelConnector && selectedModel && selectedPrompt));
  }, [selectedModelConnector, selectedModel, selectedPrompt]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${(date.getMonth()+1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
  };

  const formatProcessDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return `${(date.getMonth()+1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()} at ${date.getHours().toString().padStart(2, '0')}h${date.getMinutes().toString().padStart(2, '0')}`;
  };

  const fetchPromptText = async (promptId) => {
    if (!promptId) {
      setPromptText('');
      setOriginalPromptText('');
      return;
    }
    const token = await getAccessToken();
    if (token) {
      fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/modelprompt/${promptId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(response => response.json())
        .then(data => {
          setPromptText(data.prompt || '');
          setOriginalPromptText(data.prompt || '');
        })
        .catch(error => console.error('Error fetching prompt text:', error));
    }
  };
  
  // 
  const fetchModelConnectorsModels = async (mcId) => {
    if (!mcId) {
      return;
    }
    const token = await getAccessToken();
    if (token) {
      fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/modelconnector/${mcId}/models`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(response => response.json())
        .then(data => {
          // const availableModels = data.params?.models?.filter(model => !model.deprecated) || [];
          setModels(data || []);
        })
        .catch(error => console.error('Error fetching model connector:', error));
    }
  };


  const fetchChunkModels = async (mcId) => {
    if (!mcId) return;
    const token = await getAccessToken();
    if (token) {
      fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/modelconnector/${mcId}/models`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(response => response.json())
        .then(data => setChunkModels(data || []))
        .catch(error => console.error('Error fetching chunk models:', error));
    }
  };


  useEffect(() => {
    const fetchConfig = async () => {
      if (justCreatedAgent) {
        setJustCreatedAgent(false);
        return;
      }
      
      const token = await getAccessToken();
      if (token) {
        if (agent.id !== '00000000000000000000000000000000') {
          fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/knowledgebase/${agent.id}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
            .then(response => response.json())
            .then(async (data) => {
              setConfig(data);
              setOriginalConfig({ display_name: data.display_name, description: data.description, ds_id: data.ds_id });
              
              // Fetch process configurations
              const processConfigs = [];
              let crawlerDsId = data.ds_id;
              for (const pc of data.process_configs || []) {
                try {
                  const response = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/processconfig/${pc.pc_id}`, {
                    headers: {
                      'Authorization': `Bearer ${token}`
                    }
                  });
                  const processData = await response.json();
                  processConfigs.push(processData);
                  if (processData.type === 'PARTITION') {
                    if (processData.params?.content_type) {
                      setSelectedOutputType(processData.params.content_type);
                      setOriginalOutputType(processData.params.content_type);
                    }
                  }
                  if (processData.type === 'CRAWLER') {
                    setConfig(prevConfig => ({...prevConfig, ds_id: processData.ds_id}));
                    crawlerDsId = processData.ds_id;
                  }
                  if (processData.type === 'CHUNK') {
                    setSelectedChunkModelConnector(processData.mc_id || '');
                    setSelectedChunkModel(processData.params?.model_id || '');
                    setOriginalChunkSettings({ mc_id: processData.mc_id || '', model_id: processData.params?.model_id || '' });
                    if (processData.mc_id) {
                      fetchChunkModels(processData.mc_id);
                    }
                  }
                  if (processData.type === 'TRANSFORMER') {
                    setTransformEnabled(true);
                    setConfig(prevConfig => ({...prevConfig, sp_id: processData.sp_id}));
                    setSelectedProvider(processData.sp_id);
                    setConfig(prevConfig => ({...prevConfig, mp_id: processData.mp_id}));
                    setSelectedPrompt(processData.mp_id);
                    setConfig(prevConfig => ({...prevConfig, mc_id: processData.mc_id}));
                    setSelectedModelConnector(processData.mc_id);
                    if (processData.mp_id) {
                      fetchPromptText(processData.mp_id);
                    }
                    if (processData.mc_id) {
                      fetchModelConnectorsModels(processData.mc_id);
                    }
                    if (processData.params?.schemas) {
                      setJsonEditorContent(processData.params.schemas);
                      setOriginalJsonEditorContent(processData.params.schemas);
                    }
                    if (processData.params?.model_id) {
                      setSelectedModel(processData.params.model_id);
                    }
                    if (processData.new_only !== undefined) {
                      setNewOnly(processData.new_only);
                    }
                    if (processData.params?.transform_tables) {
                      setSelectedTables(processData.params.transform_tables);
                    }
                    setOriginalTransformerSettings({
                      sp_id: processData.sp_id || '',
                      md_id: processData.params?.model_id || '',
                      mp_id: processData.mp_id || '',
                      mc_id: processData.mc_id || '',
                      new_only: processData.new_only || false,
                      transform_tables: processData.params?.transform_tables || []
                    });
                    // setConfig(prevConfig => ({...prevConfig, sp_id: processData.mp_id}));
                    // setSelectedProvider(processData.mp_id);
                  }
                } catch (error) {
                  console.error('Error fetching process configuration:', error);
                }
              }
              setConfig(prevConfig => ({...prevConfig, process_configs: processConfigs}));
              setProcessConfigs(processConfigs);
              setOriginalConfig(prev => ({...prev, ds_id: crawlerDsId}));
            })
            .catch(error => console.error('Error fetching agent config:', error));
          
          fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/knowledgebase/${agent.id}/processes/stats`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
            .then(response => response.json())
            // .then(data => setUsage({ indexed: data.indexed || 0, last_indexed: data.last_indexed }))
            .then(data => setUsage(data))
            .catch(error => console.error('Error fetching usage:', error));
          
          fetch(`${process.env.REACT_APP_TABLES_API_EP}/knowledgebase/${agent.id}/partitions/tables/metadata`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
            .then(response => response.json())
            .then(data => setTablesMetadata(data))
            .catch(error => console.error('Error fetching tables metadata:', error));
        }
        
        fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/datasource`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
          .then(response => response.json())
          .then(data => setDatasources(data))
          .catch(error => console.error('Error fetching datasources:', error));
        
        fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/serviceprovider?type=llm`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
          .then(response => response.json())
          .then(data => setProviders(data))
          .catch(error => console.error('Error fetching providers:', error));
        
        fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/modelconnector`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
          .then(response => response.json())
          .then(data => setModelConnectors(data))
          .catch(error => console.error('Error fetching model connectors:', error));
        
        fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/modelprompt`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
          .then(response => response.json())
          .then(data => setPrompts(data))
          .catch(error => console.error('Error fetching prompts:', error));
      }
    };
    fetchConfig();
  }, [agent.id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowDropdown(false);
        setShowProviderDropdown(false);
        setShowModelConnectorDropdown(false);
        setShowPromptDropdown(false);
        setShowOutputTypeDropdown(false);
        setShowChunkModelConnectorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // useEffect(() => {
  //   if (config.sp_id) {
  //     setSelectedProvider(config.sp_id);
  //   }
  // }, [config.sp_id]);

  const handleEditJsonSchema = async () => {
    const token = await getAccessToken();
    if (token && config.ds_id) {
      fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/datasource/${config.ds_id}/scan`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(response => response.json())
        .then(data => {
          const paths = data.content ? data.content.map(item => item.path) : [];
          setSchemaPaths(paths);
          setShowJsonSchemaModal(true);
        })
        .catch(error => console.error('Error fetching schema paths:', error));
    }
  };

  const closeJsonSchemaModal = () => {
    setShowJsonSchemaModal(false);
    setSchemaPaths([]);
    setSelectedPath('');
    setSchemaFields([]);
    setSelectedFields([]);
    setPathFilter('');
    setFieldFilter('');
    setSampleData(null);
    setTableName('');
  };

  const handlePathSelect = async (path) => {
    setSelectedPath(path);
    const token = await getAccessToken();
    if (token) {
      const response = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/datasource/${config.ds_id}/content/jsonschema/?path=${encodeURIComponent(path)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSchemaFields(data['content'] || []);
        setSelectedFields([]);
      } else {
        const errorData = await response.json();
        setPromptErrorMessage(errorData.message || 'Unable to retrieve the schema.');
        setPromptErrorTitle('Error retrieving schema');
        setShowPromptErrorModal(true);
      }
    }
  };

  const handleFieldToggle = (field) => {
    setSelectedFields(prev => 
      prev.includes(field) 
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  const handleAddSchema = () => {
    if (!tableName.trim()) {
      setTableNameError(true);
      return;
    }
    setJsonEditorContent(prev => ({
      ...prev,
      [tableName]: selectedFields
    }));
    setIsModified(true);
    if (onUnsavedChange) onUnsavedChange(true);
    closeJsonSchemaModal();
  };

  const handleSavePrompt = async () => {
    if (!selectedPrompt) {
      setShowSaveNewPromptModal(true);
      return;
    }
    const token = await getAccessToken();
    if (token && selectedPrompt) {
      const response = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/modelprompt/${selectedPrompt}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ prompt: promptText })
      });

      if (response.ok) {
        setOriginalPromptText(promptText);
      } else {
        const errorData = await response.json();
        setPromptErrorMessage(errorData.message || 'Unable to save the instructions.');
        setPromptErrorTitle('Error saving the instructions');
        setShowPromptErrorModal(true);
      }
    }
  };

  const handleSave = async () => {
    // Validate required fields
    const isDisplayNameEmpty = !config.display_name?.trim();
    const isDescriptionEmpty = !config.description?.trim();
    
    setDisplayNameError(isDisplayNameEmpty);
    setDescriptionError(isDescriptionEmpty);
    
    if (isDisplayNameEmpty || isDescriptionEmpty) {
      return;
    }

    const modifiedFields = {};
    if (config.display_name !== originalConfig.display_name) {
      modifiedFields.display_name = config.display_name;
    }
    if (config.description !== originalConfig.description) {
      modifiedFields.description = config.description;
    }

    const dsIdChanged = config.ds_id !== originalConfig.ds_id;
    const jsonEditorChanged = JSON.stringify(jsonEditorContent) !== JSON.stringify(originalJsonEditorContent);
    const outputTypeChanged = selectedOutputType !== originalOutputType;
    let crawlerProcess = processConfigs.find(pc => pc.type === 'CRAWLER');
    let transformerProcess = processConfigs.find(pc => pc.type === 'TRANSFORMER');
    let partitionProcess = processConfigs.find(pc => pc.type === 'PARTITION');
    const chunkProcess = processConfigs.find(pc => pc.type === 'CHUNK');

    const transformerSettingsChanged = 
      selectedProvider !== originalTransformerSettings.sp_id ||
      selectedModelConnector !== originalTransformerSettings.mc_id ||
      selectedModel !== originalTransformerSettings.md_id ||
      selectedPrompt !== originalTransformerSettings.mp_id ||
      newOnly !== originalTransformerSettings.new_only ||
      JSON.stringify(selectedTables) !== JSON.stringify(originalTransformerSettings.transform_tables);

    const chunkSettingsChanged =
      selectedChunkModelConnector !== originalChunkSettings.mc_id ||
      selectedChunkModel !== originalChunkSettings.model_id;

    if (Object.keys(modifiedFields).length === 0 && !dsIdChanged && !jsonEditorChanged && !transformerSettingsChanged && !outputTypeChanged && !chunkSettingsChanged) {
      setShowNoChangesModal(true);
      return;
    }

    const token = await getAccessToken();
    if (token) {
      try {
        let success = true;
        let errorMessage = '';

        if (agent.id === '00000000000000000000000000000000') {
          
          // Create new agent
          const response = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/knowledgebase?initialize=1`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(modifiedFields)
          });

          if (response.status === 201) {
            let newAgent = await response.json();
            newAgent.ds_id = config.ds_id // capture before resetting it with the new agent configurations and saving it to the crawler process right after
            agent.id = newAgent.id;
            setJustCreatedAgent(true);
            setConfig(newAgent);
            
            // Fetch process configuration for new crawler process added to the agent (aka knowlwedge base)
            const crawlerResponse = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/knowledgebase/${newAgent.id}/processconfig/type/CRAWLER`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            if (crawlerResponse.status === 200) {
              const crawlerData = await crawlerResponse.json();
              crawlerProcess = crawlerData;
              
              // Fetch process configuration for new transformer process
              const transformerResponse = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/knowledgebase/${newAgent.id}/processconfig/type/TRANSFORMER`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              if (transformerResponse.status === 200) {
                const transformerData = await transformerResponse.json();
                transformerProcess = transformerData;
                setProcessConfigs([crawlerData, transformerData]);
              } else {
                setProcessConfigs([crawlerData]);
              }
            }
            else {
              success = false;
              const errorData = await crawlerResponse.json();
              errorMessage = errorData.message || 'Unable to fetch the process configuration of the new crawler for the newly created agent.';
            }
          } else {
            success = false;
            const errorData = await response.json();
            errorMessage = errorData.message || 'Unable to create the agent.';
          }
        } else if (Object.keys(modifiedFields).length > 0) {
          const response = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/knowledgebase/${agent.id}`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(modifiedFields)
          });

          if (response.status !== 200) {
            success = false;
            const errorData = await response.json();
            errorMessage = errorData.message || 'Unable to save modified agent configuration';
          }
        }

        // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        // Updating CRAWLER process
        // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        if (success && dsIdChanged && crawlerProcess) {
          console.log('Updating CRAWLER process config:', crawlerProcess, 'with ds_id:', config.ds_id, 'changed: ', dsIdChanged);
          const processResponse = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/processconfig/${crawlerProcess.id}`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ ds_id: config.ds_id })
          });

          if (processResponse.status !== 200) {
            success = false;
            const errorData = await processResponse.json();
            errorMessage = errorData.message || 'Unable to save the data source id.';
          }
        }

        // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        // Create or Update TRANSFORMER process
        // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

        if (success && (jsonEditorChanged || transformerSettingsChanged)) {
          let currentTransformerProcess = transformerProcess;
          
          if (!currentTransformerProcess) {
            // Create new TRANSFORMER process
            console.log('Creating new TRANSFORMER process config:', transformerProcess, 'JSON schema changed: ', jsonEditorChanged, 'LLM settings changed: ', transformerSettingsChanged);
            const createResponse = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/processconfig`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                type: 'TRANSFORMER',
                display_name: 'Transformer for agent: ' + config.display_name,
                description: config.description,
                ...(selectedProvider && { sp_id: selectedProvider }),
                ...(selectedPrompt ? { mp_id: selectedPrompt } : { mp_id: null }),
                ...(selectedModelConnector ? { mc_id: selectedModelConnector } : { mc_id: null }),
                new_only: newOnly,
                params: { 
                  ...(jsonEditorContent && {schemas: jsonEditorContent}),
                  ...(selectedModel && { model_id: selectedModel }),
                  ...(selectedTables && {transform_tables: selectedTables})
                }
              })
            });

            // API returns an HTTPStatus.CREATED when successful:
            if (createResponse.status === 201) {
              currentTransformerProcess = await createResponse.json();
              
              // Add Transformer process to the knowledge base
              const addResponse = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/knowledgebase/${agent.id}/processconfig/${currentTransformerProcess.id}`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });

              if (addResponse.status !== 200) {
                success = false;
                const errorData = await addResponse.json();
                errorMessage = errorData.message || 'Unable to add transformer to agent.';
              }
            } else {
              success = false;
              const errorData = await createResponse.json();
              errorMessage = errorData.message || 'Unable to create transformer process.';
            }
          } else {
            // Update existing TRANSFORMER process
            console.log('Updating new TRANSFORMER process config:', transformerProcess, 'JSON schema changed: ', jsonEditorChanged, 'LLM settings changed: ', transformerSettingsChanged);
            const processResponse = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/processconfig/${currentTransformerProcess.id}`, {
              method: 'PATCH',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ 
                ...(selectedProvider && { sp_id: selectedProvider }),
                mp_id: selectedPrompt || null,
                mc_id: selectedModelConnector || null,
                new_only: newOnly,
                params: { 
                  ...(jsonEditorContent && {schemas: jsonEditorContent}),
                  ...(selectedModel && { model_id: selectedModel }),
                  ...(selectedTables && {transform_tables: selectedTables})
                }
              })
            });

            if (processResponse.status !== 200) {
              success = false;
              const errorData = await processResponse.json();
              errorMessage = errorData.message || 'Unable to save JSON schemas for tables.';
            }
          }
        }

        // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        // Update PARTITION process
        // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        if (success && outputTypeChanged) {
          if (!partitionProcess) {
            console.error('No PARTITION process configured for this agent.');
            success = false;
            errorMessage = 'A PARTITIONER process has not been configured for this agent.';
          } else {
            const partitionResponse = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/processconfig/${partitionProcess.id}`, {
              method: 'PATCH',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ params: { content_type: selectedOutputType } })
            });
            if (partitionResponse.status !== 200) {
              success = false;
              const errorData = await partitionResponse.json();
              errorMessage = errorData.message || 'Unable to save the output content type.';
            }
          }
        }

        // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        // Update CHUNK process
        // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        if (success && chunkSettingsChanged) {
          if (!chunkProcess) {
            console.error('No CHUNK process configured for this agent.');
            success = false;
            errorMessage = 'A CHUNK process has not been configured for this agent.';
          } else {
            const chunkResponse = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/processconfig/${chunkProcess.id}`, {
              method: 'PATCH',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                mc_id: selectedChunkModelConnector || null,
                params: { ...chunkProcess.params, model_id: selectedChunkModel || null }
              })
            });
            if (chunkResponse.status !== 200) {
              success = false;
              const errorData = await chunkResponse.json();
              errorMessage = errorData.message || 'Unable to save the chunk model settings.';
            }
          }
        }

        if (success) {
          setSaveSuccess(true);
          setSaveMessage('Configuration has been saved successfully.');
          setOriginalConfig({ display_name: config.display_name, description: config.description, ds_id: config.ds_id });
          setOriginalJsonEditorContent(jsonEditorContent);
          setOriginalOutputType(selectedOutputType);
          setOriginalChunkSettings({ mc_id: selectedChunkModelConnector, model_id: selectedChunkModel });
          setIsModified(false);
          if (onUnsavedChange) onUnsavedChange(false);
          
          // // Update info-box data after successful save
          // if (agent.id !== '00000000000000000000000000000000') {
          //   setConfig(prevConfig => ({
          //     ...prevConfig,
          //     modified: new Date().toISOString()
          //   }));
          // }
        } else {
          setSaveSuccess(false);
          setSaveMessage(errorMessage);
        }
      } catch (error) {
        console.log("Unable to save the content: ", error)
        setSaveSuccess(false);
        setSaveMessage('Unable to save the content. Houston we have a problem: ' + error.message);
      }
      setShowSaveModal(true);
    }
  };

  const handleShowDataSample = async () => {
    if (selectedFields.length === 0) return;
    const token = await getAccessToken();
    if (token) {
      fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/datasource/${config.ds_id}/content/filterjson/?path=${encodeURIComponent(selectedPath)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedFields)
      })
        .then(response => response.json())
        .then(data => setSampleData(data['content']))
        .catch(error => console.error('Error fetching sample data:', error));
    }
  };

  const handleCancel = () => {
    if (isModified) {
      setShowCancelModal(true);
    } else {
      onCancel();
    }
  };

  const handleSaveNewPrompt = async () => {
    const token = await getAccessToken();
    if (token) {
      const response = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/modelprompt`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          display_name: newPromptName,
          description: newPromptDescription,
          prompt: promptText,
          type: "system" // set to "system" by default, instead of "human"
        })
      });

      if (response.ok) {
        const newPrompt = await response.json();
        setSelectedPrompt(newPrompt.id);
        setConfig(prevConfig => ({...prevConfig, mp_id: newPrompt.id}));
        setOriginalPromptText(promptText);
        setPrompts(prev => [...prev, newPrompt]);
        setShowSaveNewPromptModal(false);
        setNewPromptName('');
        setNewPromptDescription('');
        setIsModified(true);
        if (onUnsavedChange) onUnsavedChange(true);
      } else {
        const errorData = await response.json();
        setPromptErrorMessage(errorData.message || 'Unable to save the new instructions.');
        setPromptErrorTitle('Error saving the instructions');
        setShowPromptErrorModal(true);
        setShowSaveNewPromptModal(false);
      }
    }
  };

  // Limit test on a sample of content from the data source: last 10 
  const handleTestContentList = async () => {
    setShowTestModal(true);
    setTestPathsLoading(true);
    const token = await getAccessToken();
    if (token && config.ds_id) {
      fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/datasource/${config.ds_id}/scan?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(async response => {
          if (response.ok) {
            const data = await response.json();
            setTestPaths(data.content || []);
          } else {
            const errorData = await response.json();
            setPromptErrorTitle('Unable to retrieve list of content');
            setPromptErrorMessage(errorData.message || 'You may need to re-authorize the access to the data source.');
            setShowTestModal(false);
            setShowPromptErrorModal(true);
          }
          setTestPathsLoading(false);
        })
        .catch(error => {
          console.error('Error fetching test paths:', error);
          setTestPathsLoading(false);
        });
    }
  };

  const closeTestModal = () => {    setShowTestModal(false);
    setTestPaths([]);
    setSelectedTestPath('');
    setTestPathFilter('');
    setTestResult('');
    setTestFileListError(false);
    setTestLoading(false);
    setTestPathsLoading(false);
  };

  const handleTest = async () => {
    if (!selectedTestPath) {
      setTestFileListError(true);
      setTimeout(() => setTestFileListError(false), 2000);
      return;
    }
    setTestLoading(true);
    setTestResult('loading');
    const token = await getAccessToken();
    if (token) {
      try {
        const response = await fetch(`${process.env.REACT_APP_PROCESS_MGR_EP}/knowledgebase/${agent.id}/evaluate?path_id=${encodeURIComponent(selectedTestPath)}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (response.ok) {
          setTestResult(JSON.stringify(data, null, 2));
        } else {
          setTestResult(data.message || 'Error occurred during test');
          setPromptErrorTitle('Unable to process selected content');
          setPromptErrorMessage(data.message || 'Error occurred during test');
          console.log('Test error: ', data)
          setShowPromptErrorModal(true);
        }
      } catch (error) {
        setTestResult('Error: ' + error.message);
      }
    }
    setTestLoading(false);
  };



  return (
    <div className='edit-page'>
      <div className="header-layout">
        <h2 className="header-title">Edit Knowledge Agent</h2>
      </div>
      <div className="edit-form">
        <div className="edit-form-row-1">
          <div className="edit-form-column-1">
            <div className="form-field">
              <label className="form-label">Name</label>
              <input 
                type="text" 
                value={config.display_name} 
                onChange={(e) => { 
                  setConfig({...config, display_name: e.target.value}); 
                  setIsModified(true);
                  if (onUnsavedChange) onUnsavedChange(true);
                }}
                onFocus={() => setDisplayNameError(false)}
                // className="form-input"
                placeholder={displayNameError ? 'Required' : ''}
                className={displayNameError ? 'form-input-error' : 'form-input'}
              />
            </div>
            <div className="form-field">
              <label className="form-label">Goal</label>
              <textarea 
                value={config.description} 
                onChange={(e) => { 
                  setConfig({...config, description: e.target.value}); 
                  setIsModified(true);
                  if (onUnsavedChange) onUnsavedChange(true);
                }}
                onFocus={() => setDescriptionError(false)}
                // className="form-input"
                rows="5"
                placeholder={descriptionError ? 'Required' : ''}
                className={descriptionError ? 'form-input-error' : 'form-input'}
              />
            </div>
            <div className="form-field dropdown datasource-field">
              <div className="datasource-field-flex">
                <label className="form-label">Data Source</label>
                <div className="dropdown-container">
                  <div 
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="dropdown-trigger"
                  >
                    {config.ds_id ? (
                      <>
                        <img src={`/media/${datasources.find(ds => ds.id === config.ds_id)?.type.toLowerCase()}-icon.png`} alt="datasource" className="icon-small" />
                        {datasources.find(ds => ds.id === config.ds_id)?.display_name}
                      </>
                    ) : 'Select a datasource'}
                  </div>
                  {showDropdown && (
                    <div className="dropdown-menu">
                      {datasources.map((ds) => (
                        <div 
                          key={ds.id} 
                          onClick={() => { 
                            setConfig({...config, ds_id: ds.id}); 
                            setShowDropdown(false); 
                            setIsModified(true);
                            if (onUnsavedChange) onUnsavedChange(true);
                          }}
                          className="dropdown-option"
                        >
                          <img src={`/media/${ds.type.toLowerCase()}-icon.png`} alt={ds.type} className="icon-small" />
                          {ds.display_name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button 
                onClick={() => config.ds_id && window.open(`/?page=datasources&dsId=${config.ds_id}`, '_blank')}
                className="btn-magnifier"
                disabled={!config.ds_id}
              >
                <img src="/media/magnifier.png" alt="View datasource" className="icon-medium" />
              </button>
            </div>
            <div className="form-field">
              <div className="info-box">
                <div><strong>Created:</strong> {formatDate(config.created)}</div>
                <div><strong>Modified:</strong> {formatDate(config.modified)}</div>
                <div><strong>ID: </strong> {config.id || agent.id}</div>
                <div><strong>Processes:</strong> {processConfigs.length}</div>
              </div>
            </div>
            <div className="button-group">
              <button 
                onClick={handleCancel}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button 
                onClick={handleSave}
                className="btn-save" 
                disabled={!isModified}
              >
                Save
              </button>
            </div>
          </div>
          <div className="edit-form-column-2">
          </div>
          <div className="edit-form-column-3">
            <div className="section-title-row">
              <div className="section-title">Usage
              </div>
              <button 
                // onClick={() => onViewUsage && onViewUsage({...config, id: agent.id})}
                onClick={() => onViewUsage && onViewUsage(config)}
                className="btn-usage"
                // disabled={!usage.last_indexed}
              >
              <img src="/media/logview.png" alt="View logs" className="icon-medium" />
              </button>
            </div>
            <table className="usage-table">
              <tbody>
                {Object.keys(usage).map((key) => (
                  <tr key={key}>
                    <td className="usage-label">{key.charAt(0).toUpperCase() + key.slice(1)}</td>
                    <td className="usage-value usage-number">{usage[key]?.count ? usage[key].count.toLocaleString() : '-'}</td>
                    <td className="usage-value usage-date">{usage[key]?.last ? formatProcessDate(usage[key].last) : 'Never'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-spacer"></div>
            <div className="section-title-row">
              <div className="section-title">Tables</div>
              <button 
                id="view-tables-btn" 
                className="btn-usage"
                onClick={() => onViewTables && onViewTables(config)}
              >
                <img src="/media/tableview.png" alt="View tables" className="icon-medium" />
              </button>
            </div>
            <table className="usage-table">
              <tbody>
                <tr>
                  <td className="usage-label">Number of tables</td>
                  <td></td>
                  <td className="usage-value usage-number">{Object.keys(tablesMetadata).length}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="edit-form-row-2">
          <div className="tab-container">
            <ul className="tab-list">
              <li 
                className={`tab ${activeTab === 'TRANSFORM' ? 'active' : ''}`}
                onClick={() => setActiveTab('TRANSFORM')}
              >
                TRANSFORM
              </li>
              <li 
                className={`tab ${activeTab === 'INDEX' ? 'active' : ''}`}
                onClick={() => setActiveTab('INDEX')}
              >
                INDEX
              </li>
            </ul>
          </div>
          <div className="tab-content">
            {activeTab === 'TRANSFORM' ? (
              <div className="tab-content-layout">

                <div className="tab-column-0">
                  <div className="form-field">
                    <label className="form-label">
                      <input 
                        type="checkbox" 
                        checked={newOnly}
                        onChange={(e) => {
                          setNewOnly(e.target.checked);
                          setIsModified(true);
                          if (onUnsavedChange) onUnsavedChange(true);
                        }}
                        /> Transform only new content
                    </label>
                  </div>
                  <div className="form-field">
                    <button 
                      onClick={handleTestContentList}
                      className="btn-save"
                    >
                      Test Transformation
                    </button>
                  </div>
                </div>

                <div className="tab-column-1">
                  <div className="form-field json-schema-field">
                    <label className="form-label">
                      <input 
                        id="extract-json-checkbox"
                        type="checkbox" 
                        checked={datasources.find(ds => ds.id === config.ds_id)?.params?.include_glob?.includes('*.json') || false}
                        readOnly
                      /> Extract data from JSON files
                    </label>
                    <button 
                      id="edit-json-schema"
                      className="btn-magnifier"
                      title="Edit JSON schema"
                      onClick={handleEditJsonSchema}
                    >
                      <img src="/media/edit-pencil.png" alt="Edit JSON schema" className="icon-medium" />
                    </button>
                  </div>
                  <div className="form-field form-field-with-gap">
                  {/* <div className="form-field"> */}
                    <div className="json-schemas-editor">
                      <Editor
                        defaultLanguage="json"
                        theme="vs-dark"
                        value={JSON.stringify(jsonEditorContent, null, 2)}
                        onChange={(value) => {
                          try {
                            setJsonEditorContent(JSON.parse(value || '{}'));
                          } catch (e) {
                            // Keep current content if invalid JSON
                          }
                          setIsModified(true);
                          if (onUnsavedChange) onUnsavedChange(true);
                        }}
                        options={{ lineNumbers: 'off', minimap: { enabled: false } }}
                      />
                    </div>
                  </div>
                </div>
                <div className="tab-column-2">
                  <div className="tab-column-2-row-1">
                    <div className="form-field">
                      <label className="form-label">
                        <input 
                          id="transform-llm-checkbox"
                          type="checkbox" 
                          checked={transformEnabled}
                          disabled={!transformEnabled}
                          onChange={(e) => {
                            if (!e.target.checked) {
                              setSelectedModelConnector('');
                              setSelectedModel('');
                              setSelectedPrompt('');
                              setPromptText('');
                              setModels([]);
                              setSelectedOutputType('');
                              setIsModified(true);
                              if (onUnsavedChange) onUnsavedChange(true);
                            }
                          }}
                        /> Transform content via LLM
                      </label>
                    </div>
                  </div>
                  <div className="tab-column-2-row-2">
                    <div className="tab-column-2-row-2-col-1">
                      <div className="form-field dropdown">
                        <label className="form-label">Model Connector</label>
                        <div className="dropdown-container">
                          <div 
                            onClick={() => setShowModelConnectorDropdown(!showModelConnectorDropdown)}
                            className="dropdown-trigger"
                          >
                            {selectedModelConnector ? (
                              <>
                                <img src={`/media/${modelConnectors.find(mc => mc.id === selectedModelConnector)?.provider.toLowerCase()}-icon.png`} alt="" className="icon-small" />
                                {modelConnectors.find(mc => mc.id === selectedModelConnector)?.display_name}
                                {!modelConnectors.find(mc => mc.id === selectedModelConnector)?.is_active && (
                                  <span className="agent-edit-inactive-warning">Inactive - Pick a different model</span>
                                )}
                              </>
                            ) : 'Select model connector'}
                          </div>
                          {showModelConnectorDropdown && (
                            <div className="dropdown-menu">
                              <div 
                                onClick={() => { 
                                  setSelectedModelConnector('');
                                  setConfig({...config, mc_id: ''});
                                  setShowModelConnectorDropdown(false);
                                  setModels([]);
                                  setIsModified(true);
                                  if (onUnsavedChange) onUnsavedChange(true);
                                }}
                                className="dropdown-option"
                              >
                                Select model connector
                              </div>
                              {modelConnectors.map((mc) => (
                                <div 
                                  key={mc.id} 
                                  onClick={() => { 
                                    setSelectedModelConnector(mc.id);
                                    setConfig({...config, mc_id: mc.id});
                                    setShowModelConnectorDropdown(false);
                                    fetchModelConnectorsModels(mc.id);
                                    setIsModified(true);
                                    if (onUnsavedChange) onUnsavedChange(true);
                                  }}
                                  className="dropdown-option"
                                >
                                  <img src={`/media/${mc.provider.toLowerCase()}-icon.png`} alt={mc.provider} className="icon-small" />
                                  {mc.display_name}
                                  {!mc.is_active && (
                                    <span className="agent-edit-inactive-warning">Inactive - Pick a different model</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="form-field">
                        <label className="form-label">Model</label>
                        <select 
                          className="form-input"
                          value={selectedModel}
                          onChange={(e) => {
                            setSelectedModel(e.target.value);
                            setIsModified(true);
                            if (onUnsavedChange) onUnsavedChange(true);
                          }}
                        >
                          <option value="">Select model</option>
                          {models.map((model) => (
                            <option key={model.model_id} value={model.model_id}>
                              {model.model_id} (${model.price_in}/${model.price_out})
                            </option>
                          ))}
                        </select>
                      </div>
                      {jsonEditorContent && Object.keys(jsonEditorContent).length > 0 && (
                        <div className="form-field form-field-table-list">
                          <label className="form-label">Select tables to augment:</label>
                          {Object.keys(jsonEditorContent).map((tableName) => (
                            <div key={tableName}>
                              <label>
                                <input 
                                  type="checkbox" 
                                  checked={selectedTables.includes(tableName)}
                                  onChange={(e) => {
                                    setSelectedTables(prev => 
                                      e.target.checked 
                                        ? [...prev, tableName]
                                        : prev.filter(t => t !== tableName)
                                    );
                                    setIsModified(true);
                                    if (onUnsavedChange) onUnsavedChange(true);
                                  }}
                                />
                                {tableName} ({Object.values(tablesMetadata).find(t => t.name === tableName)?.records || 0})
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="tab-column-2-row-2-col-2">
                      <div className="form-field dropdown agent-edit-prompt-row">
                        <div className="agent-edit-prompt-dropdown">
                          <label className="form-label">Select instructions</label>
                          <div className="dropdown-container">
                            <div 
                              onClick={() => setShowPromptDropdown(!showPromptDropdown)}
                              className="dropdown-trigger"
                            >
                              {selectedPrompt ? (
                                prompts.find(p => p.id === selectedPrompt)?.display_name
                              ) : 'Select instructions'}
                            </div>
                            {showPromptDropdown && (
                              <div className="dropdown-menu">
                                <div 
                                  className="dropdown-option"
                                  onClick={() => { 
                                    setSelectedPrompt('');
                                    setShowPromptDropdown(false);
                                    setConfig(prevConfig => ({...prevConfig, mp_id: ''}));
                                    setPromptText('');
                                    setIsModified(true);
                                    if (onUnsavedChange) onUnsavedChange(true);
                                  }}
                                >
                                  Select instructions
                                </div>
                                {prompts.map((prompt) => (
                                  <div 
                                    className="dropdown-option"
                                    key={prompt.id} 
                                    onClick={() => { 
                                      setSelectedPrompt(prompt.id); 
                                      setShowPromptDropdown(false);
                                      setConfig(prevConfig => ({...prevConfig, mp_id: prompt.id}));
                                      fetchPromptText(prompt.id);
                                      setIsModified(true);
                                      if (onUnsavedChange) onUnsavedChange(true);
                                    }}
                                  >
                                    {prompt.display_name}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <button 
                          onClick={handleSavePrompt}
                          className="btn-save agent-edit-save-prompt-btn"
                          disabled={promptText === originalPromptText}
                        >
                          Save instructions
                        </button>
                      </div>
                      <div className="form-field form-field-full-height">
                        <label className="form-label">Instructions</label>
                        <textarea 
                          className="form-input textarea-full-height"
                          value={promptText}
                          onChange={(e) => setPromptText(e.target.value)}
                          />
                        <div id="output-type-dropdown" className="form-field dropdown output-type-dropdown">
                          {/* <label className="form-label">Expected generated content type</label> */}
                          <div className="dropdown-container">
                            <div
                              onClick={() => setShowOutputTypeDropdown(!showOutputTypeDropdown)}
                              className="dropdown-trigger"
                              >
                              {selectedOutputType ? { text: 'Text', markdown: 'Markdown', csv: 'Table' }[selectedOutputType] : 'Select expected generated content type'}
                            </div>
                            {showOutputTypeDropdown && (
                              <div className="dropdown-menu">
                                <div className="dropdown-option" onClick={() => { setSelectedOutputType(''); setShowOutputTypeDropdown(false); setIsModified(true); if (onUnsavedChange) onUnsavedChange(true); }}>Select expected generated content type</div>
                                {[{ label: 'Text', value: 'text' }, { label: 'Markdown', value: 'markdown' }, { label: 'Table', value: 'csv' }].map(opt => (
                                  <div key={opt.value} className="dropdown-option" onClick={() => { setSelectedOutputType(opt.value); setShowOutputTypeDropdown(false); setIsModified(true); if (onUnsavedChange) onUnsavedChange(true); }}>{opt.label}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="tab-content-layout">
                <div className="tab-column-1">
                  <div className="form-field dropdown">
                    <label className="form-label">Model Connector</label>
                    <div className="dropdown-container">
                      <div
                        onClick={() => setShowChunkModelConnectorDropdown(!showChunkModelConnectorDropdown)}
                        className="dropdown-trigger"
                      >
                        {selectedChunkModelConnector ? (
                          <>
                            <img src={`/media/${modelConnectors.find(mc => mc.id === selectedChunkModelConnector)?.provider.toLowerCase()}-icon.png`} alt="" className="icon-small" />
                            {modelConnectors.find(mc => mc.id === selectedChunkModelConnector)?.display_name}
                          </>
                        ) : 'Select model connector'}
                      </div>
                      {showChunkModelConnectorDropdown && (
                        <div className="dropdown-menu">
                          <div
                            onClick={() => { setSelectedChunkModelConnector(''); setChunkModels([]); setSelectedChunkModel(''); setShowChunkModelConnectorDropdown(false); setIsModified(true); if (onUnsavedChange) onUnsavedChange(true); }}
                            className="dropdown-option"
                          >
                            Select model connector
                          </div>
                          {modelConnectors.filter(mc => mc.type === 'embedding').map((mc) => (
                            <div
                              key={mc.id}
                              onClick={() => {
                                setSelectedChunkModelConnector(mc.id);
                                setSelectedChunkModel('');
                                setChunkModels([]);
                                setShowChunkModelConnectorDropdown(false);
                                fetchChunkModels(mc.id);
                                setIsModified(true);
                                if (onUnsavedChange) onUnsavedChange(true);
                              }}
                              className="dropdown-option"
                            >
                              <img src={`/media/${mc.provider.toLowerCase()}-icon.png`} alt={mc.provider} className="icon-small" />
                              {mc.display_name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-field">
                    <label className="form-label">Model</label>
                    <select
                      className="form-input"
                      value={selectedChunkModel}
                      onChange={(e) => { setSelectedChunkModel(e.target.value); setIsModified(true); if (onUnsavedChange) onUnsavedChange(true); }}
                    >
                      <option value="">Select model</option>
                      {chunkModels.map((model) => (
                        <option key={model.model_id} value={model.model_id}>
                          {model.model_id} (${model.price_in}/${model.price_out})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showJsonSchemaModal && (
        <div className="modal-overlay">
          <div className={`modal-content ${selectedPath && schemaFields.length > 0 ? 'modal-content-wide' : ''} ${sampleData ? 'modal-content-tall' : ''}`}>
            <h3>Add a new JSON schema</h3>
            <div className="modal-body">
              <div className="modal-file-section">
                <p>Select a JSON file:</p>
                <input 
                  type="text" 
                  placeholder="Type to filter list" 
                  value={pathFilter}
                  onChange={(e) => setPathFilter(e.target.value)}
                  className="modal-path-filter"
                />
                <div className="modal-file-list">
                  {schemaPaths
                    .filter(path => path.toLowerCase().includes(pathFilter.toLowerCase()))
                    .map((path, index) => (
                    <div 
                      key={index} 
                      onClick={() => handlePathSelect(path)}
                      className={`modal-file-item ${selectedPath === path ? 'modal-file-item-selected' : ''}`}
                    >
                      {path}
                    </div>
                  ))}
                </div>
              </div>
              {selectedPath && schemaFields.length > 0 && (
                <div className="modal-fields-section">
                  <p>Select fields:</p>
                  <input 
                    type="text" 
                    placeholder="Type to filter the list of fields..." 
                    value={fieldFilter}
                    onChange={(e) => setFieldFilter(e.target.value)}
                    className="modal-path-filter"
                  />
                  <div className="modal-fields-container">
                    {schemaFields
                      .filter(field => field.toLowerCase().includes(fieldFilter.toLowerCase()))
                      .map((field, index) => (
                      <label key={index} className="modal-field-label">
                        <input 
                          type="checkbox"
                          checked={selectedFields.includes(field)}
                          onChange={() => handleFieldToggle(field)}
                          className="modal-field-checkbox"
                        />
                        {field}
                      </label>
                    ))}
                  </div>
                  <button 
                    className="btn-save agent-edit-show-sample-btn" 
                    onClick={handleShowDataSample}
                    disabled={selectedFields.length === 0}
                  >
                    Show Data Sample
                  </button>
                </div>
              )}
            </div>
            {sampleData && (
              <div>
                <h4>Data Sample</h4>
                <div className="json-schema-table-wrapper">
                  <table id="json-schema-data-sample-table" className="data-table">
                  <thead>
                    <tr>
                      {Object.keys(sampleData).map(column => (
                        <th key={column}>{column}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: Math.max(...Object.values(sampleData).map(obj => Object.keys(obj).length)) }, (_, rowIndex) => (
                      <tr key={rowIndex}>
                        {Object.keys(sampleData).map(column => {
                          const value = sampleData[column][rowIndex];
                          const displayValue = Array.isArray(value) ? value.join(', ') : (value || '');
                          return <td key={column}>{displayValue}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                  </table>
                </div>
                <div className="json-schema-table-name-wrapper">
                  <div id="json-schema-table-name-container">
                    <label className="form-label">Table Name</label>
                    <input 
                      id="json-schema-table-name-input"
                      type="text" 
                      value={tableName}
                      onChange={(e) => setTableName(e.target.value)}
                      onFocus={() => setTableNameError(false)}
                      // className="form-input"
                      placeholder={tableNameError ? 'Required' : ''}
                      className={tableNameError ? 'form-input-error' : 'form-input'}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="modal-buttons">
              <button onClick={closeJsonSchemaModal} className="btn-cancel">Cancel</button>
              <button onClick={handleAddSchema} className="btn-save" disabled={selectedFields.length === 0}>Add Schema</button>
            </div>
          </div>
        </div>
      )}
      {showSaveModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-compact">
            <h3>{saveSuccess ? 'Success' : 'Error'}</h3>
            <p>{saveMessage}</p>
            <div className="modal-buttons">
              <button onClick={() => setShowSaveModal(false)} className="btn-save">OK</button>
            </div>
          </div>
        </div>
      )}
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-compact">
            <h3>Unsaved Changes</h3>
            <p>You have unsaved changes. Are you sure you want to leave without saving?</p>
            <div className="modal-buttons">
              <button onClick={() => setShowCancelModal(false)} className="btn-cancel">Stay</button>
              <button onClick={() => { setShowCancelModal(false); onCancel(); }} className="btn-save">Leave</button>
            </div>
          </div>
        </div>
      )}
      {showNoChangesModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-compact">
            <h3>No Changes</h3>
            <p>There are no changes to be saved.</p>
            <div className="modal-buttons">
              <button onClick={() => setShowNoChangesModal(false)} className="btn-save">OK</button>
            </div>
          </div>
        </div>
      )}
      {showPromptErrorModal && (
        <div className="modal-overlay" style={{ zIndex: 1100 }}>
          <div className="modal-content modal-content-compact">
            <h3>{promptErrorTitle}</h3>
            <p>{promptErrorMessage}</p>
            <div className="modal-buttons">
              <button onClick={() => setShowPromptErrorModal(false)} className="btn-save">OK</button>
            </div>
          </div>
        </div>
      )}
      {showSaveNewPromptModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-compact">
            <h3>Save new instructions</h3>
            <div className="form-field">
              <label className="form-label">Name</label>
              <input 
                type="text" 
                value={newPromptName}
                onChange={(e) => setNewPromptName(e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-field">
              <label className="form-label">Description</label>
              <textarea 
                value={newPromptDescription}
                onChange={(e) => setNewPromptDescription(e.target.value)}
                className="form-input"
                rows="3"
              />
            </div>
            <div className="modal-buttons">
              <button onClick={() => setShowSaveNewPromptModal(false)} className="btn-cancel">Cancel</button>
              <button onClick={handleSaveNewPrompt} className="btn-save">Save</button>
            </div>
          </div>
        </div>
      )}
      {showTestModal && (
        <div className="modal-overlay">
          <div className={`modal-content ${testResult ? 'modal-content-wide' : ''}`}>
            <h3>Test Transformation</h3>
            {testPathsLoading ? (
              <div>
                <span className="spinner"></span>
                <p>Fetching a sample of the content (files or emails) from the data source...</p>
              </div>
            ) : (
              <>
                <div className="modal-body">
                  <div className="modal-file-section">
                    <p>Select one content (file or email):</p>
                    <input 
                      type="text" 
                      placeholder="Type to filter list" 
                      value={testPathFilter}
                      onChange={(e) => setTestPathFilter(e.target.value)}
                      className="modal-path-filter"
                    />
                    <div className="modal-file-list" style={testFileListError ? { border: '2px solid #DD2461' } : {}}>
                      {testPaths
                        .filter(item => (item.name || item.path || item).toLowerCase().includes(testPathFilter.toLowerCase()))
                        .map((item, index) => {
                          const path = typeof item === 'string' ? item : item.path;
                          const displayName = typeof item === 'string' ? item : (item.name || item.path);
                          return (
                            <div 
                              key={index} 
                              onClick={() => setSelectedTestPath(path)}
                              className={`modal-file-item ${selectedTestPath === path ? 'modal-file-item-selected' : ''}`}
                            >
                              {displayName}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                  {testResult && (
                    <div className="modal-file-section">
                      <p>
                        Result:
                        {testLoading && <span className="spinner"></span>}
                      </p>
                      {testResult !== 'loading' && (
                        <div className="json-editor-wrapper">
                          <div className="json-result-editor">
                            <Editor
                              defaultLanguage="json"
                              theme="vs-dark"
                              value={testResult}
                              options={{ 
                                wordWrap: 'off',
                                readOnly: true, 
                                lineNumbers: 'off', 
                                minimap: { enabled: false },
                                scrollBeyondLastLine: false,
                                automaticLayout: true
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="modal-buttons">
                  <button onClick={closeTestModal} className="btn-cancel">Cancel</button>
                  <button onClick={handleTest} className="btn-save" disabled={testLoading}>
                    {testLoading ? 'Testing...' : 'Test'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentEdit;